import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Color } from 'src/constants';
import { color } from 'src/constants/color.css';

import { RouterLink } from './RouterLink';
import Text from './Text';

type Tab = Readonly<{
  key?: string;
  label: React.ReactNode;
  icon?: (props: { color: string; size: number }) => React.ReactNode;
  to?: string;
  onClick?: () => unknown;
  selected?: boolean;
  count?: number;
}>;

type Props = Readonly<{
  tabs: ReadonlyArray<Tab>;
  accentColor?: string;
  alwaysShowCount?: boolean;
  /**
   * Tabs share the available width evenly
   */
  fullWidth?: boolean;
  /**
   * Tab contents are centred, rather than starting from the left
   */
  centered?: boolean;
}>;

export default function Tabs({
  tabs,
  accentColor,
  alwaysShowCount,
  fullWidth,
  centered,
}: Props) {
  return (
    <html.div
      style={[
        (fullWidth || centered) && styles.flexContainer,
        centered && styles.centeredContainer,
      ]}
    >
      {tabs.map((tab, i) => (
        <TabItem
          key={tab.key ?? i}
          tab={tab}
          accentColor={accentColor}
          alwaysShowCount={alwaysShowCount}
          fullWidth={fullWidth}
          centered={centered}
        />
      ))}
    </html.div>
  );
}

function TabItem({
  tab,
  accentColor,
  alwaysShowCount,
  fullWidth,
  centered,
}: {
  tab: Tab;
  accentColor?: string;
  alwaysShowCount?: boolean;
  fullWidth?: boolean;
  centered?: boolean;
}) {
  const style = [
    styles.tab,
    fullWidth && styles.fullWidthTab,
    centered && styles.centeredTab,
    tab.selected && styles.selected,
    tab.selected &&
      (accentColor ? styles.selectedBorder(accentColor) : undefined),
  ];

  const content = (
    <>
      {tab.icon?.({
        size: 20,
        color: tab.selected ? Color.neutral100 : Color.neutral80,
      })}
      <Text
        typeface={tab.selected ? 'body.medium' : 'body.regular'}
        color={tab.selected ? 'neutral100' : 'neutral80'}
        style={!tab.selected && styles.unselectedHover}
      >
        {tab.label}
      </Text>
      {tab.count !== undefined &&
        tab.count > 0 &&
        (alwaysShowCount || tab.selected) && (
          <html.span style={styles.count}>
            <Text typeface="body.small.medium" color="neutral100">
              {tab.count}
            </Text>
          </html.span>
        )}
    </>
  );

  if (tab.to) {
    return (
      <RouterLink to={tab.to} onClick={tab.onClick}>
        <html.span style={style}>{content}</html.span>
      </RouterLink>
    );
  }

  return (
    <html.span onClick={tab.onClick} style={style}>
      {content}
    </html.span>
  );
}

const styles = css.create({
  flexContainer: {
    display: 'flex',
  },
  centeredContainer: {
    justifyContent: 'center',
  },
  fullWidthTab: {
    flexGrow: 1,
    flexBasis: 0,
  },
  centeredTab: {
    justifyContent: 'center',
  },
  tab: {
    paddingTop: 10,
    paddingBottom: 14,
    paddingInline: 8,
    borderBottomWidth: 3,
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    transition: 'all 0.25s',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  count: {
    paddingInline: 6,
    backgroundColor: color.neutral20,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unselectedHover: {
    color: color.neutral80,
    ':hover': {
      color: color.neutral100,
    },
  },
  selected: {
    borderBottomColor: color.accent,
    ':hover': {
      borderBottomColor: color.accent,
    },
  },
  selectedBorder: (borderColor: string) => ({
    borderBottomColor: borderColor,
    ':hover': {
      borderBottomColor: borderColor,
    },
  }),
});
